import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js'
import { DitherAsciiShader } from './shaders/ditherAsciiShader.js'
import { CompositeShader } from './shaders/compositeShader.js'
import { WorldPosMaterial } from './shaders/worldPosMaterial.js'
import { createLightformers } from './lightformers.js'
import { createFluidDisplacement } from './fluidDisplacement.js'
import { params, saveParams } from './config/persistence.js'
import { initControls } from './ui/controls.js'
import { isDebug, createOverlay } from './ui/overlay.js'
import { startTransition, updateTransition } from './transition.js'
import '@niccolofanton/driftpane/theme.css'

// --- Loader ---
const loaderOverlay = document.createElement('div')
loaderOverlay.className = 'loader-overlay'
loaderOverlay.innerHTML = `
    <span class="loader-label">Loading</span>
    <div class="loader-track"><div class="loader-bar"></div></div>
    <span class="loader-size"></span>
`
document.body.appendChild(loaderOverlay)
const loaderBar = loaderOverlay.querySelector('.loader-bar')
const loaderSizeLabel = loaderOverlay.querySelector('.loader-size')

const formatMB = (bytes) => (bytes / (1024 * 1024)).toFixed(1) + ' MB'
const showLoader = () => {
    loaderBar.style.width = '0%'
    loaderSizeLabel.textContent = ''
    loaderOverlay.classList.add('visible')
}
const updateLoader = (loaded, total) => {
    const pct = Math.round((loaded / total) * 100)
    loaderBar.style.width = pct + '%'
    loaderSizeLabel.textContent = `${formatMB(loaded)} / ${formatMB(total)}`
}
const hideLoader = () => { loaderOverlay.classList.remove('visible') }

showLoader()

// --- Canvas & Renderer ---
const canvas = document.querySelector('canvas.viewport')
const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' })
renderer.setSize(window.innerWidth, window.innerHeight)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5))
renderer.toneMapping = THREE.NoToneMapping
renderer.outputColorSpace = THREE.LinearSRGBColorSpace

// --- Scene ---
const scene = new THREE.Scene()
scene.background = new THREE.Color(params.backgroundColor)

// --- Camera ---
const camera = new THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 0.1, 100)
camera.position.set(0, 6, 0)
camera.lookAt(0, 0, 0)

// --- Controls ---
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.dampingFactor = 0.05
controls.target.set(0, 0, 0)
controls.minDistance = 3
controls.maxDistance = 20
controls.maxPolarAngle = Math.PI * 0.45
controls.enableRotate = params.enableRotate
controls.enableZoom = params.enableZoom
controls.enablePan = params.enablePan
controls.autoRotate = params.autoRotate
controls.autoRotateSpeed = params.autoRotateSpeed

// --- Lighting ---
const ambientLight = new THREE.AmbientLight(0xffffff, 0.4)
scene.add(ambientLight)
const dirLight = new THREE.DirectionalLight(0xffffff, 1.2)
dirLight.position.set(5, 10, 5)
scene.add(dirLight)

// --- Environment map ---
const pmremGenerator = new THREE.PMREMGenerator(renderer)
pmremGenerator.compileEquirectangularShader()
new RGBELoader().load('./studio.hdr', (hdrTexture) => {
    hdrTexture.mapping = THREE.EquirectangularReflectionMapping
    const envMap = pmremGenerator.fromEquirectangular(hdrTexture).texture
    scene.environment = envMap
    hdrTexture.dispose()
    pmremGenerator.dispose()
})

// --- Lightformers ---
const lightformers = createLightformers(scene)

// --- Mouse trail buffer ---
const fluid = createFluidDisplacement(renderer, 128, 128)

// --- Render targets for materialMode (selective rendering) ---
// maskRT: full-res + MSAA 4x → smooth car silhouette for the composite.
//   Cheap because the override material is MeshBasicMaterial.
// worldPosRT: half-res. Used as noise input only, gated by maskRT in shader.
// bgRT: quarter-res. Soft backdrop (ground plane + HDR), composited under
//   the car via the MSAA mask, so high res is wasted fillrate.
const MASK_SCALE = 0.5
const BG_SCALE = 0.25
const pr = renderer.getPixelRatio()
const rtOpts = { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, type: THREE.HalfFloatType }
const _wFull = window.innerWidth * pr
const _hFull = window.innerHeight * pr
const _wHalf = Math.max(1, Math.round(_wFull * MASK_SCALE))
const _hHalf = Math.max(1, Math.round(_hFull * MASK_SCALE))
const _wBg = Math.max(1, Math.round(_wFull * BG_SCALE))
const _hBg = Math.max(1, Math.round(_hFull * BG_SCALE))
const bgRT = new THREE.WebGLRenderTarget(_wBg, _hBg, rtOpts)
const maskRT = new THREE.WebGLRenderTarget(_wFull, _hFull,
    { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, samples: 4 })
const maskMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff })
const worldPosRT = new THREE.WebGLRenderTarget(_wHalf, _hHalf,
    { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, type: THREE.HalfFloatType })

// --- Track ground planes and car meshes ---
const groundPlanes = []
const carMeshes = []
// materialMode read directly from params.materialMode (no separate var)

// --- Load model ---
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/')
const gltfLoader = new GLTFLoader()
gltfLoader.setDRACOLoader(dracoLoader)

let carModel = null

gltfLoader.load('./model.glb', (gltf) => {
    hideLoader()
    carModel = gltf.scene

    const box = new THREE.Box3().setFromObject(carModel)
    const center = box.getCenter(new THREE.Vector3())
    const size = box.getSize(new THREE.Vector3())
    const maxDim = Math.max(size.x, size.y, size.z)
    const scale = 4 / maxDim

    carModel.scale.setScalar(scale)
    carModel.position.sub(center.multiplyScalar(scale))
    carModel.position.y = -(box.min.y * scale)
    carModel.rotation.y = Math.PI

    carModel.traverse((child) => {
        if (child.isMesh) {
            // Ground plane is the only huge flat mesh (scale ~7x10x8)
            const ws = new THREE.Vector3()
            child.getWorldScale(ws)
            const isGround = Math.max(ws.x, ws.y, ws.z) > 2
            if (isGround) {
                child.userData.isGroundPlane = true
                child.material = child.material.clone()
                groundPlanes.push(child)
            } else {
                carMeshes.push(child)
                if (child.material) {
                    child.material.envMapIntensity = params.envMapIntensity
                }
            }
        }
    })

    scene.add(carModel)
    cameraDirty = true

    const markDirty = () => { shaderDirty = true; cameraDirty = true }

    if (isDebug) {
        initControls({
            renderer, controls, ditherPass, lightformers, carModel, scene,
            markDirty,
        })
    }

    createOverlay({ markDirty })
}, (progress) => {
    if (progress.lengthComputable) updateLoader(progress.loaded, progress.total)
})

// --- Post-processing (screen mode) ---
const composer = new EffectComposer(renderer)
composer.addPass(new RenderPass(scene, camera))

const ditherPass = new ShaderPass(DitherAsciiShader)
ditherPass.uniforms.uResolution.value.set(window.innerWidth * pr, window.innerHeight * pr)
composer.addPass(ditherPass)

// --- Selective compositing (materialMode) ---
const carComposer = new EffectComposer(renderer)
const carRenderPass = new RenderPass(scene, camera)
carComposer.addPass(carRenderPass)
const carDitherPass = new ShaderPass(DitherAsciiShader)
carDitherPass.uniforms.uResolution.value.set(window.innerWidth * pr, window.innerHeight * pr)
carDitherPass.uniforms.uMaskOnly.value = 0.0
carComposer.addPass(carDitherPass)
carComposer.renderToScreen = false

// Final composite: bg + processed car via mask
const compositeQuad = new THREE.Mesh(
    new THREE.PlaneGeometry(2, 2),
    new THREE.ShaderMaterial({
        vertexShader: CompositeShader.vertexShader,
        fragmentShader: CompositeShader.fragmentShader,
        uniforms: {
            tBackground: { value: null },
            tProcessed: { value: null },
            tMask: { value: null },
            uExposure: { value: 1.0 },
        },
    })
)
const compositeScene = new THREE.Scene()
compositeScene.add(compositeQuad)
const compositeCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)

// --- Apply params ---
const setColorUniform = (uniform, hex) => {
    const n = parseInt(hex.slice(1), 16)
    uniform.set(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255)
}

const applyShaderParams = (u) => {
    u.uNoiseScale.value = params.noiseScale
    u.uNoiseSpeed.value = params.noiseSpeed
    u.uNoiseThreshold.value = params.noiseThreshold
    u.uNoiseSoftness.value = params.noiseSoftness
    u.uPixelSize.value = params.pixelSize
    u.uMode.value = params.mode
    u.uToonEnabled.value = params.toonEnabled ? 1.0 : 0.0
    u.uToonLevels.value = params.toonLevels
    setColorUniform(u.uToonColor1.value, params.toonColor1)
    setColorUniform(u.uToonColor2.value, params.toonColor2)
    setColorUniform(u.uToonColor3.value, params.toonColor3)
    setColorUniform(u.uToonColor4.value, params.toonColor4)
    u.uShowPixelUnderneath.value = params.showPixelUnderneath ? 1.0 : 0.0
    u.uCharBrightnessDelta.value = params.charBrightnessDelta
    u.uCharDarker.value = params.charDarker ? 1.0 : 0.0
    u.uDisplacementStrength.value = params.trailIntensity
    u.uExposure.value = params.exposure
}

const applySceneParams = () => {
    scene.background.set(params.backgroundColor)
    controls.autoRotate = params.autoRotate
    controls.autoRotateSpeed = params.autoRotateSpeed
    controls.enableRotate = params.enableRotate
    controls.enableZoom = params.enableZoom
    controls.enablePan = params.enablePan
    if (lightformers.setSpeed) lightformers.setSpeed(params.lightformerSpeed)
    for (let i = 0; i < carMeshes.length; i++) {
        carMeshes[i].material.envMapIntensity = params.envMapIntensity
    }
    ditherPass.uniforms.uMaskOnly.value = params.maskModelOnly ? 1.0 : 0.0
}

applyShaderParams(ditherPass.uniforms)
ditherPass.uniforms.uMaskOnly.value = params.maskModelOnly ? 1.0 : 0.0
let shaderDirty = true

// --- Listen for smooth preset transitions (from overlay) ---
window.addEventListener('preset-switch', (e) => {
    cameraDirty = true
    startTransition(params, e.detail.target, () => {
        saveParams()
    })
})

// --- Listen for instant preset changes (from debug controls) ---
window.addEventListener('preset-changed', () => {
    applySceneParams()
    shaderDirty = true
    cameraDirty = true
})
window.addEventListener('toggle-rotate', (e) => {
    controls.autoRotate = e.detail
})

// --- Resize ---
const applyViewportSize = () => {
    const w = window.innerWidth
    const h = window.innerHeight
    camera.aspect = w / h
    camera.updateProjectionMatrix()
    renderer.setSize(w, h)
    composer.setSize(w, h)
    carComposer.setSize(w, h)
    const _pr = renderer.getPixelRatio()
    const pw = w * _pr, ph = h * _pr
    ditherPass.uniforms.uResolution.value.set(pw, ph)
    carDitherPass.uniforms.uResolution.value.set(pw, ph)
    maskRT.setSize(pw, ph)
    const mw = Math.max(1, Math.round(pw * MASK_SCALE))
    const mh = Math.max(1, Math.round(ph * MASK_SCALE))
    worldPosRT.setSize(mw, mh)
    const bw = Math.max(1, Math.round(pw * BG_SCALE))
    const bh = Math.max(1, Math.round(ph * BG_SCALE))
    bgRT.setSize(bw, bh)
    // Cached passes need to be re-rendered after a resize
    cameraDirty = true
}
window.addEventListener('resize', applyViewportSize)

// --- Render loop ---
const clock = new THREE.Clock()
// Fixed-timestep simulation for framerate-independent trail + controls.
// Render runs at display rate, but mouse trail dissipation/diffusion and
// OrbitControls damping/auto-rotate update at a fixed 60 Hz so the result
// is identical at any display refresh rate.
const SIM_HZ = 60
const FIXED_DT = 1 / SIM_HZ
const MAX_SUB_STEPS = 5
let simAccumulator = 0

// --- Mask/worldPos cache (camera-only dependence) ---
// maskRT and worldPosRT depend on camera + model geometry only, not time.
// We can skip rendering them when the camera hasn't moved.
// Epsilon-based comparison: OrbitControls damping leaves residual ~1e-15
// deltas every frame, so exact equality never holds even at rest.
const _lastCamPos = new THREE.Vector3(NaN, NaN, NaN)
const _lastCamQuat = new THREE.Quaternion()
const CAM_POS_EPS_SQ = 1e-8 // ~1e-4 distance
const CAM_ROT_EPS = 1e-6
let cameraDirty = true
const checkCameraDirty = () => {
    if (cameraDirty) return true
    const dPos = _lastCamPos.distanceToSquared(camera.position)
    const d = Math.abs(_lastCamQuat.dot(camera.quaternion))
    const dRot = 1 - d
    if (dPos > CAM_POS_EPS_SQ) return true
    if (dRot > CAM_ROT_EPS) return true
    return false
}
const markCameraClean = () => {
    _lastCamPos.copy(camera.position)
    _lastCamQuat.copy(camera.quaternion)
    cameraDirty = false
}

// --- Adaptive pixel ratio (FPS-based, with hysteresis) ---
const MAX_PR = Math.min(window.devicePixelRatio || 1, 1.5)
const MIN_PR = 0.75
const PR_STEP = 0.25
let currentPR = MAX_PR
const PR_WINDOW = 30          // frames to average
const PR_LOW_DT = 1 / 45      // > 22ms avg → too slow, drop PR
const PR_HIGH_DT = 1 / 110    // < 9ms avg  → headroom, raise PR
let prSampleSum = 0
let prSampleCount = 0
let prCooldown = 0
const setPixelRatio = (newPR) => {
    if (newPR === currentPR) return
    currentPR = newPR
    renderer.setPixelRatio(newPR)
    applyViewportSize()
}
const adaptPixelRatio = (delta) => {
    if (prCooldown > 0) { prCooldown--; return }
    prSampleSum += delta
    prSampleCount++
    if (prSampleCount < PR_WINDOW) return
    const avg = prSampleSum / prSampleCount
    prSampleSum = 0
    prSampleCount = 0
    if (avg > PR_LOW_DT && currentPR > MIN_PR) {
        setPixelRatio(Math.max(MIN_PR, Math.round((currentPR - PR_STEP) * 100) / 100))
        prCooldown = PR_WINDOW
    } else if (avg < PR_HIGH_DT && currentPR < MAX_PR) {
        setPixelRatio(Math.min(MAX_PR, Math.round((currentPR + PR_STEP) * 100) / 100))
        prCooldown = PR_WINDOW
    }
}
const _savedClear = new THREE.Color()
const _savedClear2 = new THREE.Color()

// Render only the model mask (white silhouette on black) — used in screen mode.
// Camera-only dependence, so we cache when the camera is static.
const renderMaskOnly = () => {
    if (!checkCameraDirty()) return
    for (let i = 0; i < groundPlanes.length; i++) groundPlanes[i].visible = false
    const origBg = scene.background
    scene.background = null
    renderer.getClearColor(_savedClear2)
    const prevAlpha = renderer.getClearAlpha()

    scene.overrideMaterial = maskMaterial
    renderer.setRenderTarget(maskRT)
    renderer.setClearColor(0x000000, 1)
    renderer.clear()
    renderer.render(scene, camera)

    scene.overrideMaterial = null
    scene.background = origBg
    renderer.setClearColor(_savedClear2, prevAlpha)
    renderer.setRenderTarget(null)
    for (let i = 0; i < groundPlanes.length; i++) groundPlanes[i].visible = true
}

// Render mask + world-space positions — used in material mode.
// Both depend on camera + model only, so we cache when camera is static.
const renderMaskAndWorldPos = () => {
    if (!checkCameraDirty()) return
    for (let i = 0; i < groundPlanes.length; i++) groundPlanes[i].visible = false
    const origBg = scene.background
    scene.background = null
    renderer.getClearColor(_savedClear2)
    const prevAlpha = renderer.getClearAlpha()

    scene.overrideMaterial = maskMaterial
    renderer.setRenderTarget(maskRT)
    renderer.setClearColor(0x000000, 1)
    renderer.clear()
    renderer.render(scene, camera)

    scene.overrideMaterial = WorldPosMaterial
    renderer.setRenderTarget(worldPosRT)
    renderer.clear()
    renderer.render(scene, camera)

    scene.overrideMaterial = null
    scene.background = origBg
    renderer.setClearColor(_savedClear2, prevAlpha)
    renderer.setRenderTarget(null)
    for (let i = 0; i < groundPlanes.length; i++) groundPlanes[i].visible = true
}

// --- Rendering helpers for crossfade ---
const renderScreenMode = () => {
    ditherPass.uniforms.tModelMask.value = maskRT.texture
    ditherPass.uniforms.uUseWorldPos.value = 0.0
    ditherPass.uniforms.uMaskOnly.value = params.maskModelOnly ? 1.0 : 0.0
    composer.render()
}

const renderMaterialMode = () => {
    // Car pass — render full scene so reflections/clearcoat are correct.
    // The composite mask separates car from bg afterward.
    carDitherPass.uniforms.tWorldPos.value = worldPosRT.texture
    carDitherPass.uniforms.tModelMask.value = maskRT.texture
    carDitherPass.uniforms.uUseWorldPos.value = 1.0
    carComposer.render()

    // bg pass — cached when camera is static (lightformer movement on the
    // ground plane is subtle enough to be invisible at quarter-res, and the
    // car covers most of what the user is looking at anyway).
    if (checkCameraDirty()) {
        for (let i = 0; i < carMeshes.length; i++) carMeshes[i].visible = false
        renderer.setRenderTarget(bgRT)
        renderer.clear()
        renderer.render(scene, camera)
        renderer.setRenderTarget(null)
        for (let i = 0; i < carMeshes.length; i++) carMeshes[i].visible = true
    }

    // composite
    const compUniforms = compositeQuad.material.uniforms
    compUniforms.tBackground.value = bgRT.texture
    compUniforms.tProcessed.value = carComposer.readBuffer.texture
    compUniforms.tMask.value = maskRT.texture
    compUniforms.uExposure.value = params.exposure
}

const frame = () => {
    // Cap delta to avoid spiral-of-death after tab backgrounding.
    const delta = Math.min(clock.getDelta(), 0.25)
    const elapsed = clock.elapsedTime

    // --- Transition update ---
    const transitioning = updateTransition(params)
    if (transitioning) {
        applySceneParams()
        shaderDirty = true
    }

    // Trail uniforms (constant per render frame, set once before sub-steps)
    fluid.uniforms.uRadius.value = params.trailRadius
    fluid.uniforms.uStrength.value = params.trailStrength
    fluid.uniforms.uDissipation.value = params.trailDissipation
    fluid.uniforms.uDiffusion.value = params.trailDiffusion
    fluid.uniforms.uSoftness.value = params.trailSoftness
    fluid.uniforms.uAspect.value = window.innerWidth / window.innerHeight

    // --- Fixed-timestep simulation: controls + mouse trail ---
    // Runs at SIM_HZ regardless of display refresh rate so behavior is
    // identical at 30/60/120/144 Hz.
    simAccumulator += delta
    let subSteps = 0
    while (simAccumulator >= FIXED_DT && subSteps < MAX_SUB_STEPS) {
        controls.update(FIXED_DT)
        fluid.update()
        simAccumulator -= FIXED_DT
        subSteps++
    }
    if (subSteps >= MAX_SUB_STEPS) simAccumulator = 0

    lightformers.update(elapsed)

    const fluidTex = fluid.getTexture()

    if (shaderDirty) {
        applyShaderParams(ditherPass.uniforms)
        applyShaderParams(carDitherPass.uniforms)
        shaderDirty = false
    }

    ditherPass.uniforms.uTime.value = elapsed
    ditherPass.uniforms.tDisplacement.value = fluidTex
    carDitherPass.uniforms.uTime.value = elapsed
    carDitherPass.uniforms.tDisplacement.value = fluidTex

    if (!params.materialMode) {
        // --- Screen mode ---
        renderScreenMode()
        if (carModel && params.maskModelOnly) {
            renderMaskOnly()
        }
    } else {
        // --- Material mode ---
        renderMaskAndWorldPos()
        renderMaterialMode()
        renderer.render(compositeScene, compositeCamera)
    }

    // Mark camera state as captured for cached passes
    markCameraClean()

    // Adapt pixel ratio based on smoothed frame time
    adaptPixelRatio(delta)

    requestAnimationFrame(frame)
}

frame()
